import {enToLatLng} from "./sim_cam_geo.js"

export const TERRARIUM_Z = 12
export const TERRAIN_HALF_M = 1000
export const TERRAIN_SEGS = 96
export const TERRARIUM_URL =
  "https://s3.amazonaws.com/elevation-tiles-prod/terrarium"

/** Mapzen Terrarium: height_m = R*256 + G + B/256 − 32768 */
export function decodeTerrarium(r, g, b) {
  return r * 256 + g + b / 256 - 32768
}

export function lngToTileX(lng, z = TERRARIUM_Z) {
  return ((lng + 180) / 360) * 2 ** z
}

export function latToTileY(lat, z = TERRARIUM_Z) {
  const rad = (lat * Math.PI) / 180
  const s = Math.sin(rad)
  const clamped = Math.min(0.9999, Math.max(-0.9999, s))
  return ((1 - Math.log((1 + clamped) / (1 - clamped)) / (2 * Math.PI)) / 2) * 2 ** z
}

export function heightsFromRgba(rgba) {
  const n = 256 * 256
  const out = new Float32Array(n)
  for (let i = 0; i < n; i++) {
    const o = i * 4
    out[i] = decodeTerrarium(rgba[o], rgba[o + 1], rgba[o + 2])
  }
  return out
}

export function createHeightField() {
  const tiles = new Map()
  let origin = null
  let pinH = 0

  function key(tx, ty) {
    return `${tx}/${ty}`
  }

  function setTile(tx, ty, heights) {
    tiles.set(key(tx, ty), heights)
  }

  function setOrigin(lat, lng, heightM) {
    origin = {lat, lng}
    pinH = heightM
  }

  function sampleAbs(lat, lng) {
    const x = lngToTileX(lng)
    const y = latToTileY(lat)
    const tx = Math.floor(x)
    const ty = Math.floor(y)
    const fx = (x - tx) * 256
    const fy = (y - ty) * 256
    return bilinear(tx, ty, fx, fy)
  }

  function at(tx, ty, px, py) {
    const t = tiles.get(key(tx, ty))
    if (!t) return null
    const ix = Math.max(0, Math.min(255, px))
    const iy = Math.max(0, Math.min(255, py))
    return t[iy * 256 + ix]
  }

  function bilinear(tx, ty, fx, fy) {
    const x0 = Math.floor(fx)
    const y0 = Math.floor(fy)
    let x1 = x0 + 1
    let y1 = y0 + 1
    let tx1 = tx
    let ty1 = ty
    if (x1 > 255) {
      x1 = 0
      tx1 = tx + 1
    }
    if (y1 > 255) {
      y1 = 0
      ty1 = ty + 1
    }
    const h00 = at(tx, ty, x0, y0)
    const h10 = at(tx1, ty, x1, y0)
    const h01 = at(tx, ty1, x0, y1)
    const h11 = at(tx1, ty1, x1, y1)
    if (h00 == null) return null
    const hx0 = h10 == null ? h00 : h00 + (h10 - h00) * (fx - x0)
    const hx1 = h01 == null ? hx0 : h01 + ((h11 == null ? h01 : h11) - h01) * (fx - x0)
    if (h01 == null && h10 == null) return h00
    const tyf = fy - y0
    return hx0 + (hx1 - hx0) * tyf
  }

  function relAtSim(x, z) {
    if (!origin) return 0
    const en = {east: x, north: -z}
    const {lat, lng} = enToLatLng(origin.lat, origin.lng, en.east, en.north)
    const abs = sampleAbs(lat, lng)
    if (abs == null || !Number.isFinite(abs)) return 0
    return abs - pinH
  }

  return {setTile, setOrigin, sampleAbs, relAtSim, get origin() { return origin }}
}

export function tilesForSquare(lat, lng, halfM, z = TERRARIUM_Z) {
  const corners = [
    enToLatLng(lat, lng, -halfM, -halfM),
    enToLatLng(lat, lng, halfM, -halfM),
    enToLatLng(lat, lng, -halfM, halfM),
    enToLatLng(lat, lng, halfM, halfM),
  ]
  let minX = Infinity
  let maxX = -Infinity
  let minY = Infinity
  let maxY = -Infinity
  for (const c of corners) {
    const x = lngToTileX(c.lng, z)
    const y = latToTileY(c.lat, z)
    minX = Math.min(minX, Math.floor(x))
    maxX = Math.max(maxX, Math.floor(x))
    minY = Math.min(minY, Math.floor(y))
    maxY = Math.max(maxY, Math.floor(y))
  }
  const out = []
  for (let ty = minY; ty <= maxY; ty++) {
    for (let tx = minX; tx <= maxX; tx++) {
      out.push({z, tx, ty})
    }
  }
  return out
}

export async function loadTerrariumTile(z, tx, ty, fetchFn = fetch) {
  const url = `${TERRARIUM_URL}/${z}/${tx}/${ty}.png`
  const res = await fetchFn(url, {mode: "cors"})
  if (!res.ok) throw new Error(`terrarium ${res.status}`)
  const blob = await res.blob()
  const bitmap = await createImageBitmap(blob)
  const canvas = document.createElement("canvas")
  canvas.width = 256
  canvas.height = 256
  const ctx = canvas.getContext("2d", {willReadFrequently: true})
  ctx.drawImage(bitmap, 0, 0)
  const {data} = ctx.getImageData(0, 0, 256, 256)
  bitmap.close?.()
  return heightsFromRgba(data)
}

export async function loadHeightField(lat, lng, halfM = TERRAIN_HALF_M, fetchFn = fetch) {
  const field = createHeightField()
  const needed = tilesForSquare(lat, lng, halfM)
  await Promise.all(
    needed.map(async ({z, tx, ty}) => {
      const h = await loadTerrariumTile(z, tx, ty, fetchFn)
      field.setTile(tx, ty, h)
    }),
  )
  const pinAbs = field.sampleAbs(lat, lng)
  field.setOrigin(lat, lng, pinAbs == null ? 0 : pinAbs)
  return field
}

export function applyHeightToPlane(geometry, field, halfM = TERRAIN_HALF_M) {
  const pos = geometry.attributes.position
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i)
    const z = pos.getZ(i)
    pos.setY(i, field.relAtSim(x, z))
  }
  pos.needsUpdate = true
  geometry.computeVertexNormals()
  return geometry
}
